import { effect, ErrorHandler, inject, InjectionToken, Service, untracked } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { AuthService } from '@authentication';
import { Subscription } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import { PrivateMessageEventProducerService } from '../../events/private-message/private-message-event.producer.service';
import { BaseService } from '../../services/base.service';
import { SSE_ACK_EVENT, SseMessage, SseService } from '../sse';
import {
	ORDER_STATE_ACTION,
	ORDERS_RELOAD_ACTIONS,
	PRIVATE_MESSAGE_TOPIC,
	PrivateMessage,
	REMAIN_RELOAD_ACTION,
	TextMessage,
	isTextMessage,
	toPrivateMessage,
	ActionMessage
} from './private-message.model';

/** How long an identical message is treated as a duplicate of the one before it. */
export const PRIVATE_MESSAGE_TEXT_DEDUPE_WINDOW_MS = new InjectionToken<number>(
	'PRIVATE_MESSAGE_TEXT_DEDUPE_WINDOW_MS',
	{ providedIn: 'root', factory: () => 1_000 }
);

/**
 * The application's single connection to the `private` SSE topic.
 *
 * Global infrastructure, not an orders feature: it owns the connection, turns each frame into an
 * event on the shared bus, and knows nothing about who is listening. Consumers subscribe through
 * `PrivateMessageEventConsumerService`.
 *
 * **Lifecycle.** Driven by authentication, not by any component: open while the user is logged in,
 * closed otherwise.
 *
 * **Reconnects.** `SseService` owns them: it retries every error with backoff, and reopens a
 * stream the server ends cleanly. All a reconnect leaves here is a second handshake — and since
 * nothing is replayed, orders and balances are asked for again off that.
 *
 * **De-duplication.** `Text_IME` messages arrive twice, identical apart from a `type` of `'1'` and
 * `'2'`. What those distinguish is unknown, so rather than dropping `type === '2'` and discarding a
 * meaning by accident, identical messages are suppressed within a short window.
 */
@Service()
export class PrivateMessageService extends BaseService {
	private readonly sseService = inject(SseService);
	private readonly authService = inject(AuthService);
	private readonly producer = inject(PrivateMessageEventProducerService);
	private readonly dedupeWindowMs = inject(PRIVATE_MESSAGE_TEXT_DEDUPE_WINDOW_MS);
	private readonly errorHandler = inject(ErrorHandler);

	/** Resolves once, as soon as authentication finishes loading. */
	private readonly authReady$ = toObservable(this.authService.waitingForLogin).pipe(
		filter(waitingForLogin => !waitingForLogin),
		take(1)
	);

	private subscription: Subscription | null = null;

	/** Handshakes seen since `start()`. The first is the initial connect; the rest are reconnects. */
	private handshakes = 0;

	/** When each text message was last seen, keyed by `itemKey|key|value`. */
	private readonly recentText = new Map<string, number>();

	/** When each order state was last seen, keyed by `orderId|orderState|value`. */
	private readonly recentOrderStates = new Map<string, number>();

	constructor() {
		super();

		effect(() => {
			const loggedIn = this.authService.isLoggedIn();
			// `untracked` so start/stop cannot enrol what they read as a dependency of this effect.
			untracked(() => (loggedIn ? this.start() : this.stop()));
		});
	}

	// ---------------------------------------------------------------------------
	// Public API
	// ---------------------------------------------------------------------------

	/**
	 * Opens the private stream, or does nothing if it is already open. Normally called by the
	 * authentication effect above rather than by hand.
	 *
	 * Waits on `authReady$` first: the effect can fire before the token is settled, and connecting
	 * then would only earn a `401`.
	 */
	start(): void {
		if (this.subscription) {
			return;
		}

		this.handshakes = 0;

		const url = new URL('api/v1/subscribe', this.sseApiPath);
		url.searchParams.append('topics', PRIVATE_MESSAGE_TOPIC);

		this.subscription = this.authReady$
			.pipe(switchMap(() => this.sseService.connect<unknown>(url.toString())))
			.subscribe({
				next: frame => this.onFrame(frame),
				// The transport retries its own errors, so one surfacing here is a fault in this
				// chain rather than a network drop — reopening could spin. Drop the stream, freeing
				// the guard that would block a later `start()`, but report it: a broken chain is
				// otherwise indistinguishable from a quiet one.
				error: (error: unknown) => {
					this.clearSubscription();
					this.errorHandler.handleError(error);
				}
			});
	}

	/** Closes the stream and forgets everything about it. Safe to call when not running. */
	stop(): void {
		this.clearSubscription();
		this.handshakes = 0;
		this.recentText.clear();
		this.recentOrderStates.clear();
	}

	private clearSubscription(): void {
		this.subscription?.unsubscribe();
		this.subscription = null;
	}

	// ---------------------------------------------------------------------------
	// Message handling
	// ---------------------------------------------------------------------------

	private onFrame(frame: SseMessage<unknown>): void {
		if (frame.type === SSE_ACK_EVENT) {
			this.handleHandshake();
			return;
		}

		// A topic we did not ask for, or one added upstream since. Neither is ours to interpret.
		if (frame.type !== PRIVATE_MESSAGE_TOPIC) {
			return;
		}

		const privateMessage = toPrivateMessage(frame.data);
		if (!privateMessage) {
			return;
		}

		this.handlePrivateMessage(privateMessage);
	}

	/**
	 * Every connection opens with a handshake, so a *second* one means the stream dropped and came
	 * back — retried under us by `SseService`, or reopened by the `repeat` in `start()`. Nothing is
	 * replayed, so whatever was published in between is asked for again rather than left to chance.
	 */
	private handleHandshake(): void {
		this.handshakes++;

		if (this.handshakes > 1) {
			this.producer.ordersReloadRequested('reconnect');
			this.producer.remainReloadRequested('reconnect');
		}
	}

	private handlePrivateMessage(message: PrivateMessage): void {
		if (isTextMessage(message)) {
			if (this.isDuplicateText(message)) {
				return;
			}

			this.producer.received(message);
			this.producer.textReceived({
				text: message.value,
				kind: message.key,
				type: message.type,
				itemKey: message.itemKey
			});
			return;
		}

		if (this.isDuplicateOrderState(message)) {
			return;
		}

		this.producer.received(message);

		if (ORDERS_RELOAD_ACTIONS.includes(message.value)) {
			this.producer.ordersReloadRequested(message.value);
			return;
		}

		if (message.value === REMAIN_RELOAD_ACTION) {
			this.producer.remainReloadRequested(message.value);
			this.producer.depositPortfolioReloadRequested(message.value);
			return;
		}

		if (message.value === ORDER_STATE_ACTION) {
			// An order changing state moves the buying power with it, so the remain is asked for too.
			this.producer.remainReloadRequested(message.value);

			// The state is what the message is *for*, but it has been seen missing. Nothing to
			// apply is not an error — the `received` event above still went out.
			if (message.omsOrderState) {
				this.producer.orderStateChanged(message.omsOrderState);
			}
			return;
		}

		// An action name added upstream since this was written. Ignored on purpose.
	}

	/**
	 * Keyed on `itemKey + key + value`, the only identity a text message has — the payload carries
	 * neither an id nor a timestamp. The window runs from the *first* sighting, so a message
	 * legitimately repeated later is let through rather than suppressed by its own duplicates.
	 *
	 * @returns `true` when this exact text was already seen inside the window.
	 */
	private isDuplicateText(message: TextMessage): boolean {
		const key = `${message.itemKey}|${message.key}|${message.value}`;
		const now = Date.now();

		this.pruneRecentText(now);

		const seenAt = this.recentText.get(key);
		if (seenAt !== undefined && now - seenAt < this.dedupeWindowMs) {
			return true;
		}

		this.recentText.set(key, now);
		return false;
	}

	/** As {@link isDuplicateText}, keyed on the state a repeat would re-announce. */
	private isDuplicateOrderState(message: ActionMessage): boolean {
		if (!message.omsOrderState) return false;
		const key = `${message.omsOrderState.orderId}|${message.omsOrderState.orderState}|${message.value}|${message.omsOrderState.remainingQuantity}|${message.omsOrderState.hostOrderId}`;
		const now = Date.now();

		this.pruneRecentOrderStates(now);

		const seenAt = this.recentOrderStates.get(key);
		if (seenAt !== undefined && now - seenAt < this.dedupeWindowMs) {
			return true;
		}

		this.recentOrderStates.set(key, now);
		return false;
	}

	/** Keeps the de-duplication map to the messages still inside the window. */
	private pruneRecentText(now: number): void {
		for (const [key, seenAt] of this.recentText) {
			if (now - seenAt >= this.dedupeWindowMs) {
				this.recentText.delete(key);
			}
		}
	}

	/** Keeps the de-duplication map to the messages still inside the window. */
	private pruneRecentOrderStates(now: number): void {
		for (const [key, seenAt] of this.recentOrderStates) {
			if (now - seenAt >= this.dedupeWindowMs) {
				this.recentOrderStates.delete(key);
			}
		}
	}
}
